import type { EditorExperimentsConfig } from './experiments-config';

type MultivariateConfig<Value> = Value extends boolean ? false : true;
type BooleanConfig<Value> = Value extends boolean ? true : false;

export type EditorExperimentOverridesMultivariate = {
	[K in keyof EditorExperimentsConfig as MultivariateConfig<
		EditorExperimentsConfig[K]['defaultValue']
	> extends true
		? K
		: never]: EditorExperimentsConfig[K]['defaultValue'];
};

export type EditorExperimentOverridesBoolean = {
	[K in keyof EditorExperimentsConfig as BooleanConfig<
		EditorExperimentsConfig[K]['defaultValue']
	> extends true
		? K
		: // Optional as boolean will default to true if not specified here
			never]?: EditorExperimentsConfig[K]['defaultValue'];
};

export const testMultivariateOverrides: EditorExperimentOverridesMultivariate = {
	cwr_publish_flow_live_doc_toggle_exp: 'control',
	'example-multivariate': 'one',
	'platform_editor_ai-prompts-placeholder': 'control',
	platform_editor_controls: 'control',
	cc_editor_ai_content_mode: 'control',
	platform_hover_card_preview_panel: 'control',
	cc_editor_insm_outlier_events: 'test',
	platform_editor_table_sticky_header_improvements: 'test_with_overflow',
	cc_fd_db_top_editor_toolbar: 'control',
	platform_editor_paste_actions_menu_v2: 'control',
	cc_smarts_should_improve_writing_migration: 'control',
	confluence_quick_insert_embeds: 'control',
	cc_cwr_prompt_strength_indicator: 'control',
	confluence_cwr_3p_connection_suggested_prompts: 'control',
};

export const testBooleanOverrides: EditorExperimentOverridesBoolean = {
	'cc-maui-ai-edit-loading-experiment': true,
	cc_maui_polish_changes_batch_1: false,
	cc_maui_polish_changes_batch_2: false,
	cc_maui_polish_changes_batch_3: false,
	cc_maui_polish_changes_batch_4: false,
	social_proof_3p_unauth_block_exp: false,
	platform_sl_3p_preauth_better_hovercard: true,
	cwr_blank_object_experiment: false,
	platform_editor_feedback_mandatory_rating: false,
	platform_editor_preview_panel_responsiveness: false,
	'enghealth-53346_fix_redaction_marker_editor': false,
	platform_editor_toolbar_aifc: false,
	'test-new-experiments-package': false,
	platform_editor_exp_lazy_node_views: false,
	platform_renderer_table_sticky_scrollbar: false,
	platform_editor_prevent_toolbar_layout_shifts: false,
	comment_on_bodied_extensions: false,
	advanced_layouts: false,
	single_column_layouts: false,
	platform_editor_ai_unsplash_page_header: false,
	platform_editor_blockquote_in_text_formatting_menu: false,
	platform_editor_ai_edit_response_in_preview: false,
	platform_editor_offline_editing_web: false,
	platform_editor_tables_drag_and_drop: false,
	platform_editor_tables_table_selector: false,
	platform_renderer_fix_analytics_memo_callback: false,
	platform_editor_no_cursor_on_edit_page_init: false,
	'jira-work-sync-desc-comment-summary': false,
	platform_editor_breakout_resizing: false,
	platform_editor_block_controls_perf_optimization: false,
	platform_editor_enable_single_player_step_merging: false,
	platform_editor_find_and_replace_improvements: false,
	platform_editor_reduce_noisy_steps_ncs: false,
	platform_editor_paste_actions_menu: false,
	platform_editor_table_fit_to_content_on_demand: false,
	platform_editor_table_fit_to_content_auto_convert: false,
	platform_editor_table_q4_loveability: false,
	platform_editor_table_menu_updates: false,
	'cwr-modal-ui-refresh': false,
	'cc-disambiguation-in-cwr': false,
	'cwr-reduce-prompt-suggestion-max-chars': false,
	'cwr-staging-area-close-as-minimize-button': false,
	prompt_tile_content_type_localizaiton: false,
	// [CCI-15904] New AIFC Editor Experience — AND-gated with
	// `platform_editor_ai_xstate_migration` (see `isNewAifcExperienceEnabled`
	// in `editor-plugin-ai/src/ui/utils/new-aifc-experience-enabled.ts`).
	// (afm-ai-pr-review-bot #2) The previous comment incorrectly referenced
	// `platform_editor_ai_replace_doc` as the AND partner; the unrelated
	// `platform_editor_ai_replace_doc: false` override has been removed since
	// it does not gate this feature.
	platform_editor_ai_new_aifc_editor_experience: false,
	cc_page_experiences_editor_image_generation: false,
	'editor-image-generation-styles-and-aspect-ratios': false,
	confluence_advanced_header_image_generation_exp: false,
	platform_use_unicode_emojis: false,
	confluence_ai_generated_emojis: false,
	platform_editor_emoji_hover_show_tooltip: false,
	'aifc-confluence-editor-csp-fix': false,
};
